import { createStore } from "vuex";
import BaseService from "@/services/base.service";
import api from "@/services/api.service";
import router from "@/router/index";
import { getAddressFromObject } from '@/composables/useGetters.js';
import { dynamicDebtorRoutes } from "@/router/debtor.js";
import { useUiStore } from "@/store/uiStore";
import { staticDebtorRoutes } from "../router/debtor";

export default createStore({
  state: {
    authenticated: false,
    token: null,
    activeComponent: null,
    clerkData: {
      cases: [],
      corporateCases: [],
      debtorViewCaseEditId: null,
      impersonated: false
    },
    caseData: null,
    requiredUploads: [],
    wvpCaseData: null,
    corporateCaseData: null,
    userData: null,
    loading: false,
    formValid: false,
    scopedFormValid: false,
    interactionState: false,
    frontendState: {
      childClickState: "INITIAL",
      alimonyClickState: "INITIAL",
      occupationClickState: "INITIAL",
      otherIncomeClickState: "INITIAL",
      assetClickState: "INITIAL",
    },
    showInformationModal: false,
    modalInfoTextKey: null,
    forms: {},
    corporatePersons: [],
    activeCase: {},
    dropdowns: [],
    showInfoModal: false,
    infoModalText: null,
  },
  getters: {
    authenticated: (state) => state.authenticated,
    token: (state) => state.token,
    role: (state) => state.userData?.role,
    impersonated: (state) => state.clerkData.impersonated,
    activeComponent: (state) => state.activeComponent,
    caseData: (state) => state.caseData,
    wvpCaseData: (state) => state.wvpCaseData,
    corporateCaseData: (state) => state.corporateCaseData,
    corporateCaseId: (state) => state.userData.corpCaseId,
    corporateCaseCurrentState: (state) => state.corporateCaseData?.currentState,
    userData: (state) => state.userData,
    loading: (state) => state.loading,
    isFormValid: (state) => (formConfigFileName) => {
      const hash = JSON.stringify(formConfigFileName).hashCode();
      return !state.forms[hash]?.form?.$invalid;
    },
    formValid: (state) => state.formValid,
    scopedFormValid: (state) => state.scopedFormValid,
    interactionState: (state) => state.interactionState,
    frontendState: (state) => state.frontendState,
    forms: (state) => state.forms,
    corporatePersons: (state) => state.corporatePersons,
    getCorporatePersonsByType: (state) => (type, flatAddress) => {
      let person = state.corporatePersons.find((item) => item.type === type);
      if (person) {
        if (flatAddress) {
          person = Object.assign(
            {},
            person,
            getAddressFromObject(person.address),
          );
        }
        return person;
      }
      return null;
    },
    dropdownByType: (state) => (type) => {
      return state.dropdowns.filter((item) => item.dropdownName === type);
    },
    activeCase: (state) => state.activeCase,
    isClerkDebtorView: (state) => {
      return (
        state.clerkData.debtorViewCaseEditId && state.userData?.role === "clerk"
      );
    }
  },
  mutations: {
    authenticate(state, { token, role }) {
      state.authenticated = true;
      state.token = token;
      state.role = role;
    },
    updateToken(state, token) { 
      sessionStorage.setItem("token", token);
      state.token = token;
    },
    setActiveComponent(state, comp) {
      state.activeComponent = comp;
    },
    removeActiveComponent(state) {
      state.activeComponent = null;
    },
    setCaseData(state, data) {
      state.caseData = data;
    },
    setWvpCaseData(state, data) {
      state.wvpCaseData = data;
    },
    setUserData(state, data) {
      state.userData = data;
    },
    triggerLoading(state) {
      state.loading = !state.loading;
    },
    setFormValid(state) {
      state.formValid = true;
    },
    setFormInvalid(state) {
      state.formValid = false;
    },
    setInteractionStateValid(state) {
      state.interactionState = true;
    },
    setInteractionStateInvalid(state) {
      state.interactionState = false;
    },
    setChildClickState(state, data) {
      state.frontendState.childClickState = data;
    },
    setAlimonyClickState(state, data) {
      state.frontendState.alimonyClickState = data;
    },
    setOccupationClickState(state, data) {
      state.frontendState.occupationClickState = data;
    },
    setOtherIncomeClickState(state, data) {
      state.frontendState.otherIncomeClickState = data;
    },
    setAssetClickState(state, data) {
      state.frontendState.assetClickState = data;
    },
    setCurrentState(state, data) {
      state.frontendState.currentState = data;
    },
    setCorporateCurrentState(state, data) {
      state.corporateCaseData.currentState = data;
    },
    closeInformationModal(state) {
      state.showInformationModal = false;
    },
    showInformationModal(state, data) {
      state.modalInfoTextKey = data;
      state.showInformationModal = true;
    },
    registerForm(state, data) {
      if (data.value.scoped) {
        state.scopedFormValid = false;
      }
      state.forms[data.key] = data.value;
    },
    unregisterForm(state, hash) {
      delete state.forms[hash];
    },
    unregisterScopedForms(state) {
      for (const [key, value] of Object.entries(state.forms)) {
        if (value.scoped) {
          delete state.forms[key];
        }
      }
      state.scopedFormValid = false;
    },
    setCorporateCaseData(state, data) {
      state.corporateCaseData = data;
    },
    setActiveCase(state, caseData) {
      state.activeCase = caseData;
    },
    setDebtorViewEditCaseId(state, id) {
      state.clerkData.debtorViewCaseEditId = id;
    },
    setImpersonation(state, flag) {
      state.clerkData.impersonated = flag;
    }
  },
  actions: {
    async retrieveUserData({ state, commit, dispatch }) {
      return new Promise((resolve, reject) => {
        BaseService.retrieve("/current-user")
          .then((res) => {
            state.userData = res.data;
            sessionStorage.setItem("userData", JSON.stringify(res.data));

            if (state.userData.role === "clerk") {
              router.push({ name: "corporate-dashboard" });
            } else if (state.userData.role === "debtor") {
              let routeName = "home";

              if (state.userData.wvpYear && state.userData.wvpCaseId) {
                routeName = "wvp-home";
              } else {
                dispatch("retrieveCaseData");
                dispatch("retrieveFrontendState");
              }

              router.push({ name: routeName });
            } else if (state.userData.role === "admin") {
              router.push("/admin/management/user");
            } else if (state.userData.role === "corp_debtor") {
              router.push({ name: "corp-start" });
            }
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    async retrieveCaseData({ state, commit }) {
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      const caseId = state.clerkData.debtorViewCaseEditId
        ? state.clerkData.debtorViewCaseEditId
        : userData.caseId;

      const res = await BaseService.find("/cases", caseId);

      state.requiredUploads = res.data.requiredUploads;

      delete res.data.requiredUploads;

      commit("setCaseData", res.data);

      const uiStore = useUiStore();

      let routes = [...dynamicDebtorRoutes];
      const routeNames = Object.keys(res.data.caseSections);

      for (let i = 0; i < routeNames.length; i++) {
        const routeName = routeNames[i];
        const showRoute = res.data.caseSections[routeName];

        if (
          ["id", "caseId", "obligationsData", "requiredUploads"].includes(routeName) ||
          showRoute
        ) {
          continue;
        }

        routes = routes.filter((route) => {
          return route.name !== routeName;
        });
      }

      routes.forEach((route) => router.addRoute("debtor-layout", route));

      for (let i = 0; i < staticDebtorRoutes.children.length; i++) {
        const staticRoute = staticDebtorRoutes.children[i];

        if (!staticRoute.meta.condition) {
          routes.push(staticRoute);
        } else {
          if (staticRoute.meta.condition?.(state.requiredUploads)) {
            routes.push(staticRoute);
          }
        }
      }

      routes = routes.sort((a, b) => a.meta.order - b.meta.order);

      for (let i = 0; i < routes.length; i++) {
        if (i >= 0 && i < routes.length - 1) {
          routes[i].meta.next = routes[i + 1].name;
        }
        if (i > 0 && i < routes.length) {
          routes[i].meta.previous = routes[i - 1].name;
        }
      }

      uiStore.routes = routes;
    },
    async retrieveFrontendState({ state }) {
      const caseId = state.clerkData.debtorViewCaseEditId
        ? state.clerkData.debtorViewCaseEditId
        : state.userData.caseId;
      
      const getFrontendState = async () => {
        const response = await api.get(`/cases/${caseId}/frontend-state`);
        state.frontendState = response.data;
        state.currentState = response.data.currentState;
      };
      
      try {
        await getFrontendState();
      } catch (error) {
        if (error.response.status === 404) {
          await api.post(`/cases/${caseId}/frontend-state`);
          await getFrontendState();
        }
      }
    },
    retrieveWvpCaseData({ state, commit }) {
      BaseService.get(
        `/cases/${state.userData.caseId}/wvp-cases/${state.userData.wvpCaseId}`,
      )
        .then((res) => {
          commit("setWvpCaseData", res.data);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async retrieveCorporateCaseData({ state, commit }) {
      const response = await BaseService.get(
        `/corp/cases/${state.userData.corpCaseId}`,
      );
      if (response) {
        if (response?.data?.companyInfo?.managementSeat) {
          response.data.companyInfo.managingDirectorAddress = true;
        } else {
          response.data.companyInfo.managingDirectorAddress = false;
        }
        commit("setCorporateCaseData", response.data);

        const personsResponse = await BaseService.get(
          `/corp/cases/${state.userData.corpCaseId}/persons`,
        );
        state.corporatePersons = personsResponse.data;

        const dropdownOptionsResponse = await BaseService.get(
          "/corp/dropdown-options",
        );
        state.dropdowns = dropdownOptionsResponse.data;
      }
    },
    retrieveWvpFrontendState({ state }) {
      BaseService.retrieve(
        `/cases/${state.userData.caseId}/wvp-frontend-state/${state.userData.wvpCaseId}`,
      )
        .then((frontendState) => {
          state.frontendState = frontendState.data;
          state.currentState = frontendState.data.currentState;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    updateFormValidity({ state }) {
      let tmpScopedValid = false;
      let tmpValid = false;
      for (const [key, value] of Object.entries(state.forms)) {
        if (value.scoped) {
          tmpScopedValid |= value.form.$invalid;
        } else {
          tmpValid |= value.form.$invalid;
        }
        value.valid |= value.form.$invalid;
      }
      state.formValid = !tmpValid;
      state.scopedFormValid = !tmpScopedValid;
    },
    retrieveClerkData({ state }) {
      const getPrivateStatus = (data) => {
        if (!data.user?.firstLogin) {
          return "RED";
        } else if (data.user?.firstLogin && !data.dataSent) {
          return "ORANGE";
        } else {
          return "GREEN";
        }
      };
      const getCorporateStatus = (data) => {
        if (!data.welcomeMailSent) {
          return "BLACK";
        } else if (!data.user?.firstLogin) {
          return "RED";
        } else if (data.user?.firstLogin && !data.dataSent) {
          return "ORANGE";
        } else {
          return "GREEN";
        }
      };
      BaseService.retrieve("/cases").then((resp) => {
        resp.data.forEach((i) => (i.status = getPrivateStatus(i)));
        state.clerkData.cases = resp.data;
      });

      BaseService.retrieve("/corp/cases").then((resp) => {
        resp.data.forEach((i) => (i.status = getCorporateStatus(i)));
        state.clerkData.corporateCases = resp.data;
      });
    },
    logout({ state }) { 
      sessionStorage.removeItem("userData");
      sessionStorage.removeItem("token");
      state.authenticated = false;
      state.token = null;
      state.role = null;
      state.userData = null;
    }
  },
  modules: {},
});
